import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import ChipsInput from "@/components/ChipsInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { COUNTRIES, COMMON_LANGUAGES, LANGUAGE_LEVELS, ARRANGEMENTS, EMPLOYMENT_TYPES, OFFICE_FREQUENCIES, TRAVEL_LEVELS, EMPLOYMENT_MECHANISMS, REMOTE_SCOPES, TIMEZONE_OFFSETS } from "@/lib/constants";
import { titleCase } from "@/lib/format";
import { Plus, Trash, FloppyDisk, UserSwitch } from "@phosphor-icons/react";

const BLANK = {
  identity: {}, languages: [], preferences: {}, professional: {}, constraints: {},
};

export default function Profile() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [cloneActive, setCloneActive] = useState(true);

  useEffect(() => {
    api.get("/profile").then((r) => setP({ ...BLANK, ...r.data,
      identity: r.data.identity || {}, preferences: r.data.preferences || {},
      professional: r.data.professional || {}, constraints: r.data.constraints || {},
      languages: r.data.languages || [] }));
    api.get("/profiles").then((r) => setProfiles(r.data.profiles));
  }, []);

  const setSec = (sec, obj) => setP((prev) => ({ ...prev, [sec]: { ...prev[sec], ...obj } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { ...p, completed: true });
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const switchProfile = async (pid) => {
    if (profiles.find((x) => x.is_active)?.id === pid) return;
    await api.post(`/profiles/${pid}/activate`);
    window.location.reload();
  };
  const createProfile = async () => {
    const { data } = await api.post("/profiles", { name: newName || "New profile", clone_active: cloneActive });
    await api.post(`/profiles/${data.id}/activate`);
    window.location.reload();
  };
  const deleteProfile = async () => {
    const active = profiles.find((x) => x.is_active);
    if (!active) return;
    try { await api.delete(`/profiles/${active.id}`); window.location.reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!p) return <p className="text-slate-400 text-sm">Loading…</p>;
  const id = p.identity, pref = p.preferences, prof = p.professional, con = p.constraints;
  const disc = p.disclosure || {};
  const setDisc = (key, val) => setP((prev) => ({ ...prev, disclosure: { ...(prev.disclosure || {}), [key]: val } }));
  const active = profiles.find((x) => x.is_active);

  const toggleArr = (sec, key, val) => {
    const arr = p[sec][key] || [];
    setSec(sec, { [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  return (
    <div>
      <PageHeader
        title="Your profile"
        subtitle="Structured facts drive eligibility and matching. You stay in control — nothing is trusted until you save it."
        action={<Button onClick={save} disabled={saving} data-testid="save-profile-btn" className="bg-slate-900 hover:bg-slate-800 gap-2"><FloppyDisk weight="fill" /> {saving ? "Saving…" : "Save profile"}</Button>}
      />

      {/* Criteria set (profile) manager */}
      <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 flex flex-col md:flex-row md:items-end gap-3" data-testid="profile-manager">
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><UserSwitch /> Active criteria set</Label>
          <Select value={active?.id || ""} onValueChange={switchProfile}>
            <SelectTrigger className="mt-1.5 max-w-sm" data-testid="profile-select"><SelectValue placeholder="Select a profile" /></SelectTrigger>
            <SelectContent>
              {profiles.map((pr) => <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-500">Name of this set</Label>
          <Input className="mt-1.5 max-w-sm" value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} placeholder="e.g. Remote career" data-testid="profile-name-input" />
        </div>
        <div className="flex gap-2">
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-slate-300 gap-1.5" data-testid="new-profile-btn"><Plus weight="bold" /> New</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New criteria set</DialogTitle></DialogHeader>
              <Label>Name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Local interim work" data-testid="new-profile-name" />
              <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 mt-2">
                <span className="text-sm text-slate-700">Start from a copy of the current set</span>
                <Switch checked={cloneActive} onCheckedChange={setCloneActive} />
              </label>
              <DialogFooter><Button onClick={createProfile} className="bg-slate-900 hover:bg-slate-800" data-testid="create-profile-confirm">Create & switch</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          {profiles.length > 1 && (
            <Button variant="outline" onClick={deleteProfile} className="border-slate-300 text-slate-500 hover:text-red-600 gap-1.5" data-testid="delete-profile-btn"><Trash /> Delete</Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="identity">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="identity" data-testid="tab-identity">Eligibility</TabsTrigger>
          <TabsTrigger value="workmodel" data-testid="tab-workmodel">Work model</TabsTrigger>
          <TabsTrigger value="languages" data-testid="tab-languages">Languages</TabsTrigger>
          <TabsTrigger value="preferences" data-testid="tab-preferences">Preferences</TabsTrigger>
          <TabsTrigger value="professional" data-testid="tab-professional">Professional</TabsTrigger>
          <TabsTrigger value="constraints" data-testid="tab-constraints">Constraints</TabsTrigger>
        </TabsList>

        {/* IDENTITY */}
        <TabsContent value="identity">
          <Card title="Identity & practical eligibility" desc="Legal and location facts that decide whether you can realistically apply.">
            <Grid>
              <SelectField label="Country of residence" value={id.country_of_residence} onChange={(v) => setSec("identity", { country_of_residence: v })} options={COUNTRIES} testid="residence-country" />
              <div>
                <Label>Work permit / visa status</Label>
                <Input className="mt-1.5" value={id.work_permit_status || ""} onChange={(e) => setSec("identity", { work_permit_status: e.target.value })} placeholder="e.g. Citizen, no permit required" data-testid="work-permit" />
              </div>
            </Grid>
            <div className="mt-4">
              <Label>Countries where you may legally work</Label>
              <ChipsInput value={id.legal_work_countries || []} onChange={(v) => setSec("identity", { legal_work_countries: v })} placeholder="Add a country" testid="legal-countries" />
            </div>
            <div className="mt-4">
              <Label>Citizenship(s)</Label>
              <ChipsInput value={id.citizenships || []} onChange={(v) => setSec("identity", { citizenships: v })} placeholder="Add a citizenship" testid="citizenships" />
            </div>
            <div className="mt-5 space-y-3">
              <ToggleRow label="I must remain based in a specific country" checked={!!id.must_remain_based} onChange={(v) => setSec("identity", { must_remain_based: v })} testid="must-remain" />
              {id.must_remain_based && (
                <SelectField label="Country I must remain in" value={id.must_remain_country} onChange={(v) => setSec("identity", { must_remain_country: v })} options={COUNTRIES} testid="must-remain-country" />
              )}
              <TriRow label="Willing to relocate" value={id.relocation_willing} onChange={(v) => setSec("identity", { relocation_willing: v })} testid="relocation-willing" />
              {id.relocation_willing === true && (
                <div>
                  <Label>Countries you would relocate to <span className="text-slate-400 font-normal">(leave empty = anywhere)</span></Label>
                  <ChipsInput value={id.relocation_countries || []} onChange={(v) => setSec("identity", { relocation_countries: v })} placeholder="Add a country" testid="relocation-countries" />
                </div>
              )}
              <ToggleRow label="I need visa / work-permit sponsorship" checked={!!id.needs_sponsorship} onChange={(v) => setSec("identity", { needs_sponsorship: v })} testid="needs-sponsorship" />
              <ToggleRow label="Holds a driving licence" checked={!!id.driving_license} onChange={(v) => setSec("identity", { driving_license: v })} testid="driving-license" />
            </div>
            <div className="mt-4">
              <Label>Travel willingness</Label>
              <Input className="mt-1.5" value={id.travel_willing || ""} onChange={(e) => setSec("identity", { travel_willing: e.target.value })} placeholder="e.g. Occasional travel acceptable" data-testid="travel-willing" />
            </div>
          </Card>
        </TabsContent>

        {/* WORK MODEL */}
        <TabsContent value="workmodel">
          <Card title="Location & work model" desc="These structured conditions feed the deterministic eligibility engine directly. Non-negotiables exclude a job; flexible ranges let a match succeed inside your stated boundary (shown as 'Flexibility used').">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField label="Preferred remote scope" value={pref.remote_scope_pref} onChange={(v) => setSec("preferences", { remote_scope_pref: v })} options={REMOTE_SCOPES} testid="wm-remote-scope" />
              <div>
                <Label>Your working timezone (UTC offset)</Label>
                <Select value={id.timezone_offset != null ? String(id.timezone_offset) : ""} onValueChange={(v) => setSec("identity", { timezone_offset: Number(v) })}>
                  <SelectTrigger className="mt-1.5" data-testid="wm-timezone"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{TIMEZONE_OFFSETS.map((o) => <SelectItem key={o} value={String(o)}>{`UTC${o >= 0 ? "+" : ""}${o}`}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label>Where you may reside / work remotely from</Label>
              <ChipsInput value={id.legal_work_countries || []} onChange={(v) => setSec("identity", { legal_work_countries: v })} placeholder="Add a country" testid="wm-residence" />
              <p className="text-xs text-slate-400 mt-1">Shared with the Eligibility tab — this is where remote work is legally/operationally permitted for you.</p>
            </div>
            <div className="mt-4">
              <Label>Where you are willing to physically work (cities / areas)</Label>
              <ChipsInput value={id.willing_work_locations || []} onChange={(v) => setSec("identity", { willing_work_locations: v })} placeholder="Add a place" testid="wm-physical" />
            </div>

            <div className="mt-5 rounded-md border border-blue-200 bg-blue-50/50 p-4">
              <p className="font-heading font-bold text-blue-900 text-sm">Flexible ranges</p>
              <p className="text-xs text-blue-700 mb-3">Boundaries you're willing to bend. A match inside these shows as "Flexibility used".</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <NumField label="Max office days / week" value={pref.max_office_days_per_week} onChange={(v) => setSec("preferences", { max_office_days_per_week: v })} testid="wm-office-days" />
                <NumField label="Max timezone gap (h)" value={con.max_timezone_gap_hours} onChange={(v) => setSec("constraints", { max_timezone_gap_hours: v })} testid="wm-tz-gap" />
                <SelectField label="Travel tolerance" value={pref.travel_tolerance} onChange={(v) => setSec("preferences", { travel_tolerance: v })} options={TRAVEL_LEVELS} testid="wm-travel" />
              </div>
              <div className="mt-4">
                <Label className="text-xs">Working-hours constraints</Label>
                <Input className="mt-1" value={pref.working_hours || ""} onChange={(e) => setSec("preferences", { working_hours: e.target.value })} placeholder="e.g. 30h/week, no weekends" data-testid="wm-hours" />
              </div>
            </div>

            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
              <p className="font-heading font-bold text-red-900 text-sm">Non-negotiables (hard)</p>
              <p className="text-xs text-red-700 mb-3">A job breaking one of these is excluded.</p>
              <ToggleRow label="No regular (weekly) office attendance" checked={!!con.no_regular_office} onChange={(v) => setSec("constraints", { no_regular_office: v })} testid="wm-no-office" light />
            </div>

            <div className="mt-4">
              <Label>Accepted engagement types (hard)</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {EMPLOYMENT_MECHANISMS.map((m) => (
                  <Chip key={m} active={(pref.employment_mechanisms || []).includes(m)} onClick={() => toggleArr("preferences", "employment_mechanisms", m)} testid={`wm-mech-${m}`}>{titleCase(m)}</Chip>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">If set, a role offered only as an unlisted type is excluded.</p>
            </div>
          </Card>
        </TabsContent>

        {/* LANGUAGES */}
        <TabsContent value="languages">
          <Card title="Languages" desc="Language requirements can be a hard eligibility blocker.">
            <div className="space-y-3">
              {(p.languages || []).map((l, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto_auto_auto] gap-3 items-end border-b border-slate-100 pb-3" data-testid={`language-row-${i}`}>
                  <SelectRaw label="Language" value={l.language} onChange={(v) => updateItem(p, setP, "languages", i, { language: v })} options={COMMON_LANGUAGES} />
                  <SelectRaw label="Level" value={l.level} onChange={(v) => updateItem(p, setP, "languages", i, { level: v })} options={LANGUAGE_LEVELS} />
                  <ToggleMini label="Spoken" checked={!!l.spoken} onChange={(v) => updateItem(p, setP, "languages", i, { spoken: v })} />
                  <ToggleMini label="Written" checked={!!l.written} onChange={(v) => updateItem(p, setP, "languages", i, { written: v })} />
                  <Button variant="ghost" size="icon" onClick={() => setP({ ...p, languages: p.languages.filter((_, x) => x !== i) })} className="text-slate-400"><Trash /></Button>
                </div>
              ))}
            </div>
            <Button variant="outline" onClick={() => setP({ ...p, languages: [...(p.languages || []), { language: "", level: "B2", spoken: true, written: true }] })} data-testid="add-language-btn" className="mt-3 border-slate-300 gap-1.5"><Plus weight="bold" /> Add language</Button>
          </Card>
        </TabsContent>

        {/* PREFERENCES */}
        <TabsContent value="preferences">
          <Card title="Work preferences" desc="These shape ranking and can be set as hard constraints on the next tab.">
            <div>
              <Label>Acceptable work arrangements</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {ARRANGEMENTS.map((a) => (
                  <Chip key={a} active={(pref.arrangements || []).includes(a)} onClick={() => toggleArr("preferences", "arrangements", a)} testid={`pref-arr-${a}`}>{titleCase(a)}</Chip>
                ))}
              </div>
            </div>
            <div className="mt-5">
              <Label>Employment types</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {EMPLOYMENT_TYPES.map((t) => (
                  <Chip key={t} active={(pref.employment_types || []).includes(t)} onClick={() => toggleArr("preferences", "employment_types", t)} testid={`pref-emp-${t}`}>{titleCase(t)}</Chip>
                ))}
              </div>
            </div>
            <div className="mt-5">
              <Label>Acceptable countries / regions</Label>
              <ChipsInput value={pref.acceptable_countries || []} onChange={(v) => setSec("preferences", { acceptable_countries: v })} placeholder="Add a country" testid="acceptable-countries" />
            </div>
            <div className="mt-4">
              <ToggleRow label="Open to cross-border remote work" checked={!!pref.cross_border_remote} onChange={(v) => setSec("preferences", { cross_border_remote: v })} testid="cross-border" />
            </div>
            <Grid className="mt-4">
              <div>
                <Label>Minimum salary</Label>
                <Input type="number" className="mt-1.5" value={pref.min_salary || ""} onChange={(e) => setSec("preferences", { min_salary: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 500000" data-testid="min-salary" />
              </div>
              <div>
                <Label>Currency</Label>
                <Input className="mt-1.5" value={pref.salary_currency || ""} onChange={(e) => setSec("preferences", { salary_currency: e.target.value })} placeholder="e.g. EUR" data-testid="salary-currency" />
              </div>
            </Grid>

            <div className="mt-6 rounded-md border border-slate-200 bg-slate-50/70 p-4" data-testid="disclosure-card">
              <p className="font-heading font-bold text-slate-800 text-sm">Privacy &amp; disclosure</p>
              <p className="text-xs text-slate-500 mb-3">You are private by default. These details are always used to find your matches, but are shared with an employer only if you allow it. Employers see the eligibility result (e.g. "meets the location requirement"), never your raw numbers or personal preferences.</p>
              <div className="space-y-2">
                <ToggleRow label="Let employers see my salary expectation" checked={!!disc.salary_expectation} onChange={(v) => setDisc("salary_expectation", v)} testid="disc-salary" light />
                <ToggleRow label="Let employers see my relocation preferences" checked={!!disc.relocation} onChange={(v) => setDisc("relocation", v)} testid="disc-relocation" light />
                <ToggleRow label="Let employers see my preferred countries" checked={!!disc.preferred_countries} onChange={(v) => setDisc("preferred_countries", v)} testid="disc-countries" light />
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* PROFESSIONAL */}
        <TabsContent value="professional">
          <Card title="Professional profile" desc="Your background powers the match — and your generated documents use only these verified facts.">
            <ChipsField label="Skills" value={prof.skills} onChange={(v) => setSec("professional", { skills: v })} testid="skills" />
            <ChipsField label="Tools / software" value={prof.tools} onChange={(v) => setSec("professional", { tools: v })} testid="tools" />
            <ChipsField label="Certifications" value={prof.certifications} onChange={(v) => setSec("professional", { certifications: v })} testid="certifications" />
            <ChipsField label="Industries" value={prof.industries} onChange={(v) => setSec("professional", { industries: v })} testid="industries" />
            <ChipsField label="Role types" value={prof.role_types} onChange={(v) => setSec("professional", { role_types: v })} testid="role-types" />
            <div className="mt-4">
              <Label>Transferable experience</Label>
              <Textarea className="mt-1.5" rows={3} value={prof.transferable_experience || ""} onChange={(e) => setSec("professional", { transferable_experience: e.target.value })} placeholder="Describe experience that transfers across roles…" data-testid="transferable" />
            </div>

            <SubHeader title="Work history" onAdd={() => setSec("professional", { work_history: [...(prof.work_history || []), {}] })} testid="add-work" />
            {(prof.work_history || []).map((w, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-4 mb-3" data-testid={`work-row-${i}`}>
                <Grid>
                  <RawInput label="Title" value={w.title} onChange={(v) => updateItem(p, setP, "professional.work_history", i, { title: v })} />
                  <RawInput label="Employer" value={w.employer} onChange={(v) => updateItem(p, setP, "professional.work_history", i, { employer: v })} />
                  <RawInput label="From (year)" value={w.start} onChange={(v) => updateItem(p, setP, "professional.work_history", i, { start: v })} />
                  <RawInput label="To (year)" value={w.end} onChange={(v) => updateItem(p, setP, "professional.work_history", i, { end: v })} />
                </Grid>
                <div className="mt-3"><RawInput label="Industry" value={w.industry} onChange={(v) => updateItem(p, setP, "professional.work_history", i, { industry: v })} /></div>
                <div className="mt-3">
                  <Label className="text-xs text-slate-500">Description</Label>
                  <Textarea rows={2} className="mt-1" value={w.description || ""} onChange={(e) => updateItem(p, setP, "professional.work_history", i, { description: e.target.value })} />
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeItem(p, setP, "professional.work_history", i)} className="mt-2 text-slate-400 gap-1"><Trash /> Remove</Button>
              </div>
            ))}

            <SubHeader title="Education" onAdd={() => setSec("professional", { education: [...(prof.education || []), {}] })} testid="add-education" />
            {(prof.education || []).map((e, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-4 mb-3" data-testid={`edu-row-${i}`}>
                <Grid>
                  <RawInput label="Degree" value={e.degree} onChange={(v) => updateItem(p, setP, "professional.education", i, { degree: v })} />
                  <RawInput label="Field" value={e.field} onChange={(v) => updateItem(p, setP, "professional.education", i, { field: v })} />
                  <RawInput label="Institution" value={e.institution} onChange={(v) => updateItem(p, setP, "professional.education", i, { institution: v })} />
                  <RawInput label="Year" value={e.year} onChange={(v) => updateItem(p, setP, "professional.education", i, { year: v })} />
                </Grid>
                <Button variant="ghost" size="sm" onClick={() => removeItem(p, setP, "professional.education", i)} className="mt-2 text-slate-400 gap-1"><Trash /> Remove</Button>
              </div>
            ))}
          </Card>
        </TabsContent>

        {/* CONSTRAINTS */}
        <TabsContent value="constraints">
          <Card title="Your criteria" desc="Non-negotiables normally exclude a job. Strong preferences affect ranking. Flexible ranges are where you'll bend.">
            <div className="rounded-md border border-red-200 bg-red-50 p-4">
              <p className="font-heading font-bold text-red-900 text-sm">Non-negotiables (hard constraints)</p>
              <p className="text-xs text-red-700 mb-3">A job failing one of these is normally excluded.</p>
              <div className="space-y-2">
                <ToggleRow label="No relocation" checked={!!con.no_relocation} onChange={(v) => setSec("constraints", { no_relocation: v })} testid="c-no-relocation" light />
                <ToggleRow label="No onsite-only roles" checked={!!con.no_onsite_only} onChange={(v) => setSec("constraints", { no_onsite_only: v })} testid="c-no-onsite" light />
                <ToggleRow label="No mandatory video application" checked={!!con.no_mandatory_video} onChange={(v) => setSec("constraints", { no_mandatory_video: v })} testid="c-no-video" light />
              </div>
              <div className="mt-3">
                <Label className="text-xs text-red-800">Other non-negotiables</Label>
                <ChipsInput value={con.hard || []} onChange={(v) => setSec("constraints", { hard: v })} placeholder="Add a hard constraint" testid="c-hard" />
              </div>
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-white p-4">
              <p className="font-heading font-bold text-slate-800 text-sm">Strong preferences</p>
              <p className="text-xs text-slate-500 mb-2">Important, but not an automatic rejection.</p>
              <ChipsInput value={con.strong || []} onChange={(v) => setSec("constraints", { strong: v })} placeholder="Add a strong preference" testid="c-strong" />
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-white p-4">
              <p className="font-heading font-bold text-slate-800 text-sm">Flexible range</p>
              <p className="text-xs text-slate-500 mb-2">Where you're willing to bend.</p>
              <ChipsInput value={con.flexible || []} onChange={(v) => setSec("constraints", { flexible: v })} placeholder="Add a flexible item" testid="c-flexible" />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---- helpers ---- */
function updateItem(p, setP, path, i, patch) {
  const parts = path.split(".");
  if (parts.length === 1) {
    const arr = [...(p[parts[0]] || [])];
    arr[i] = { ...arr[i], ...patch };
    setP({ ...p, [parts[0]]: arr });
  } else {
    const [sec, key] = parts;
    const arr = [...((p[sec] || {})[key] || [])];
    arr[i] = { ...arr[i], ...patch };
    setP({ ...p, [sec]: { ...p[sec], [key]: arr } });
  }
}
function removeItem(p, setP, path, i) {
  const [sec, key] = path.split(".");
  const arr = ((p[sec] || {})[key] || []).filter((_, x) => x !== i);
  setP({ ...p, [sec]: { ...p[sec], [key]: arr } });
}

function Card({ title, desc, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="font-heading font-bold text-lg">{title}</h2>
      {desc && <p className="text-sm text-slate-600 mt-1 mb-5">{desc}</p>}
      {children}
    </div>
  );
}
function Grid({ children, className = "" }) { return <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${className}`}>{children}</div>; }
function SubHeader({ title, onAdd, testid }) {
  return (
    <div className="flex items-center justify-between mt-6 mb-3">
      <h3 className="font-heading font-bold text-slate-800">{title}</h3>
      <Button variant="outline" size="sm" onClick={onAdd} data-testid={testid} className="border-slate-300 gap-1"><Plus weight="bold" /> Add</Button>
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5" data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
function SelectRaw({ label, value, onChange, options }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
function RawInput({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input className="mt-1" value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" className="mt-1" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={testid} />
    </div>
  );
}
function ChipsField({ label, value, onChange, testid }) {
  return (
    <div className="mt-4">
      <Label>{label}</Label>
      <ChipsInput value={value || []} onChange={onChange} testid={testid} />
    </div>
  );
}
function ToggleRow({ label, checked, onChange, testid, light }) {
  return (
    <label className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 cursor-pointer ${light ? "border-red-200 bg-white" : "border-slate-200"}`}>
      <span className="text-sm font-medium text-slate-800">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}
function ToggleMini({ label, checked, onChange }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
function TriRow({ label, value, onChange, testid }) {
  const val = value === true ? "yes" : value === false ? "no" : "unspecified";
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
      <span className="text-sm font-medium text-slate-800">{label}</span>
      <Select value={val} onValueChange={(v) => onChange(v === "yes" ? true : v === "no" ? false : null)}>
        <SelectTrigger className="w-36 h-8" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="unspecified">Unspecified</SelectItem>
          <SelectItem value="yes">Yes</SelectItem>
          <SelectItem value="no">No</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
      {children}
    </button>
  );
}
